// File: /var/www/app2/frontend/src/components/SupportRequests.tsx

import React, { useState, useEffect, FormEvent, ChangeEvent } from "react";
import axios, { AxiosError } from "axios";
import { User } from "../App";
import "../styles/SupportRequests.css";

interface SupportRequestsProps {
  user: User;
}

type RequestType = "bug" | "feature" | "change" | "other";
type RequestStatus = "open" | "in_progress" | "completed" | "closed";
type RequestPriority = "low" | "normal" | "high" | "urgent";
type StatusFilter = "all" | RequestStatus;

interface SupportRequest {
  id: number;
  user_id: number;
  user_name: string;
  user_email?: string;
  user_username?: string;
  subject: string;
  description: string;
  type: RequestType;
  status: RequestStatus;
  priority: RequestPriority;
  admin_response?: string;
  responded_by?: number;
  responded_by_username?: string;
  responded_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

interface FormData {
  subject: string;
  description: string;
  type: RequestType;
}

interface StatusCounts {
  all: number;
  open: number;
  in_progress: number;
  completed: number;
  closed: number;
}

function SupportRequests({ user }: SupportRequestsProps): React.ReactElement {
  const [requests, setRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [respondingTo, setRespondingTo] = useState<number | null>(null);
  const [response, setResponse] = useState<string>("");
  const [updating, setUpdating] = useState<boolean>(false);

  const isAdmin = user.role === "admin" || user.role === "superadmin" || user.role === "system_admin";

  // Form state
  const [formData, setFormData] = useState<FormData>({
    subject: "",
    description: "",
    type: "other"
  });

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async (): Promise<void> => {
    try {
      const response = await axios.get<{ success: boolean; requests: SupportRequest[] }>("/api/support-requests");
      const data = response.data.requests || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch support requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      alert("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post("/api/support-requests", formData);
      alert("Support request submitted successfully!");
      setFormData({ subject: "", description: "", type: "other" });
      setShowForm(false);
      fetchRequests();
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      alert("Failed to submit support request: " + (axiosError.response?.data?.error || axiosError.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (requestId: number, newStatus: RequestStatus): Promise<void> => {
    if (!window.confirm(`Change status to ${newStatus.replace('_', ' ')}?`)) {
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`/api/support-requests/${requestId}`, { status: newStatus });
      alert("Status updated successfully!");
      fetchRequests();
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      alert("Failed to update status: " + (axiosError.response?.data?.error || axiosError.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleSubmitResponse = async (requestId: number): Promise<void> => {
    if (!response.trim()) {
      alert("Please enter a response");
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`/api/support-requests/${requestId}`, {
        admin_response: response,
        status: "in_progress"
      });
      alert("Response submitted successfully!");
      setResponse("");
      setRespondingTo(null);
      fetchRequests();
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      alert("Failed to submit response: " + (axiosError.response?.data?.error || axiosError.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkComplete = async (requestId: number): Promise<void> => {
    if (!window.confirm("Mark this request as completed?")) {
      return;
    }

    setUpdating(true);
    try {
      await axios.put(`/api/support-requests/${requestId}`, { status: "completed" });
      alert("Request marked as completed!");
      fetchRequests();
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      alert("Failed to mark as completed: " + (axiosError.response?.data?.error || axiosError.message));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (requestId: number): Promise<void> => {
    if (!window.confirm("Are you sure you want to delete this support request? This action cannot be undone.")) {
      return;
    }

    setUpdating(true);
    try {
      await axios.delete(`/api/support-requests/${requestId}`);
      alert("Support request deleted!");
      fetchRequests();
    } catch (error) {
      const axiosError = error as AxiosError<{ error: string }>;
      alert("Failed to delete request: " + (axiosError.response?.data?.error || axiosError.message));
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status: RequestStatus): React.ReactElement => {
    const statusColors: Record<RequestStatus, string> = {
      open: "status-open",
      in_progress: "status-in-progress",
      completed: "status-completed",
      closed: "status-closed"
    };
    return (
      <span className={`status-badge ${statusColors[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getTypeLabel = (type: RequestType): string => {
    const types: Record<RequestType, string> = {
      bug: "Bug Report",
      feature: "Feature Request",
      change: "Change Request",
      other: "Other"
    };
    return types[type] || "Other";
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const filteredRequests = statusFilter === "all"
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const statusCounts: StatusCounts = {
    all: requests.length,
    open: requests.filter(r => r.status === 'open').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
    closed: requests.filter(r => r.status === 'closed').length,
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="support-request-container">
      <div className="support-header">
        <div className="header-left">
          <h2>{isAdmin ? "Support Requests Admin" : "Support Requests"}</h2>
          {isAdmin && (
            <div className="admin-stats">
              <span className="stat-item">Total: {requests.length}</span>
              <span className="stat-item open">Open: {statusCounts.open}</span>
              <span className="stat-item in-progress">In Progress: {statusCounts.in_progress}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="new-request-btn"
        >
          {showForm ? "Cancel" : "+ New Support Request"}
        </button>
      </div>

      {/* Filter bar for admins */}
      {isAdmin && (
        <div className="filter-bar">
          <button
            className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            All ({statusCounts.all})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'open' ? 'active' : ''}`}
            onClick={() => setStatusFilter('open')}
          >
            Open ({statusCounts.open})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'in_progress' ? 'active' : ''}`}
            onClick={() => setStatusFilter('in_progress')}
          >
            In Progress ({statusCounts.in_progress})
          </button>
          <button
            className={`filter-btn ${statusFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            Completed ({statusCounts.completed})
          </button>
        </div>
      )}

      {showForm && (
        <div className="support-form-section">
          <h3>Submit New Support Request</h3>
          <form onSubmit={handleSubmit} className="support-form">
            <div className="form-group">
              <label htmlFor="type">Request Type:</label>
              <select
                id="type"
                value={formData.type}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, type: e.target.value as RequestType })}
                className="form-control"
              >
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="change">Change Request</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject: *</label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="Brief summary of your request..."
                className="form-control"
                required
                maxLength={500}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description: *</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Please provide detailed information about your issue or request..."
                className="form-control"
                rows={8}
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Request"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="requests-list">
        {!isAdmin && <h3>My Support Requests ({requests.length})</h3>}

        {filteredRequests.length === 0 ? (
          <div className="no-requests">
            {isAdmin ? (
              <p>No {statusFilter !== 'all' ? statusFilter.replace('_', ' ') : ''} support requests found.</p>
            ) : (
              <>
                <p>You haven't submitted any support requests yet.</p>
                <p>Click "New Support Request" above to submit one.</p>
              </>
            )}
          </div>
        ) : (
          <div className="requests-grid">
            {filteredRequests.map((request) => (
              <div key={request.id} className={`request-card ${isAdmin ? 'admin-card' : ''}`}>
                <div className="request-header">
                  <div className="request-title-row">
                    <h4>{request.subject}</h4>
                    {getStatusBadge(request.status)}
                  </div>
                  <div className="request-meta">
                    <span className="request-type">{getTypeLabel(request.type)}</span>
                    {isAdmin && (
                      <span className="request-user">By: {request.user_name || request.user_username}</span>
                    )}
                    <span className="request-date">Submitted: {formatDate(request.created_at)}</span>
                    {request.priority && request.priority !== 'normal' && (
                      <span className={`priority-badge priority-${request.priority}`}>
                        {request.priority.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="request-body">
                  <div className="request-description">
                    {request.description}
                  </div>
                </div>

                {request.admin_response && (
                  <div className="admin-response">
                    <div className="response-header">
                      <strong>{isAdmin ? "Your Response:" : "Admin Response:"}</strong>
                      {request.responded_by_username && (
                        <span className="responder"> by {request.responded_by_username}</span>
                      )}
                    </div>
                    <div className="response-text">{request.admin_response}</div>
                    {request.responded_at && (
                      <div className="response-date">
                        Responded: {formatDate(request.responded_at)}
                      </div>
                    )}
                  </div>
                )}

                {/* Admin action buttons */}
                {isAdmin && (
                  <div className="request-actions">
                    {request.status === 'open' && (
                      <button
                        onClick={() => setRespondingTo(respondingTo === request.id ? null : request.id)}
                        className="respond-btn"
                      >
                        {respondingTo === request.id ? "Cancel Response" : "Respond"}
                      </button>
                    )}

                    {request.status === 'in_progress' && (
                      <button
                        onClick={() => handleMarkComplete(request.id)}
                        className="complete-btn"
                        disabled={updating}
                      >
                        Mark Complete
                      </button>
                    )}

                    {request.status !== 'completed' && request.status !== 'closed' && (
                      <button
                        onClick={() => handleUpdateStatus(request.id, 'in_progress')}
                        className="status-btn"
                        disabled={updating || request.status === 'in_progress'}
                      >
                        Mark In Progress
                      </button>
                    )}

                    <button
                      onClick={() => handleDelete(request.id)}
                      className="delete-btn"
                      disabled={updating}
                    >
                      Delete
                    </button>
                  </div>
                )}

                {/* Response form for admins */}
                {isAdmin && respondingTo === request.id && (
                  <div className="response-form">
                    <textarea
                      value={response}
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setResponse(e.target.value)}
                      placeholder="Enter your response to the user..."
                      rows={6}
                      className="response-textarea"
                    />
                    <div className="response-form-actions">
                      <button
                        onClick={() => handleSubmitResponse(request.id)}
                        className="submit-response-btn"
                        disabled={updating || !response.trim()}
                      >
                        {updating ? "Submitting..." : "Submit Response"}
                      </button>
                      <button
                        onClick={() => {
                          setRespondingTo(null);
                          setResponse("");
                        }}
                        className="cancel-response-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!isAdmin && (
                  <div className="request-footer">
                    <div className="request-dates">
                      {request.completed_at && (
                        <div>
                          <strong>Completed:</strong> {formatDate(request.completed_at)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SupportRequests;
